import {
  meetsAccentContrast,
  parseHexColour,
  type Industry,
  type WebsiteResponse,
  INDUSTRIES,
  INDUSTRY_LABELS,
} from '@groundly/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Check, CircleCheck, Copy } from 'lucide-react';
import { useContext, useState, type FormEvent, type ReactNode } from 'react';
import { z } from 'zod';

import { useMe } from '../../../../api/me.js';
import { WebsitePlanCard } from '../../../../billing/WebsitePlanCard.js';
import { SuggestedQuestionsCard } from '../../../../settings/SuggestedQuestionsCard.js';
import {
  useCompleteSetup,
  useDeleteWebsite,
  useSkipBranding,
  useUpdateBranding,
  useUpdateIndustry,
  useUpdateWebsite,
  useWebsite,
} from '../../../../api/websites.js';
import { Button } from '../../../../components/Button.js';
import { DangerAlert } from '../../../../components/DangerAlert.js';
import { PageHeading } from '../../../../components/PageHeading.js';
import { LoadFailedArea, LoadingArea } from '../../../../components/PlaceholderPage.js';
import { WebsiteStatusContext } from '../../../../components/WebsiteSection.js';
import { TextField } from '../../../../components/TextField.js';
import buttonStyles from '../../../../components/button.module.css';
import pageStyles from '../../../../components/page.module.css';
import styles from './setup.module.css';

/**
 * Website setup: guided steps 2--4 (when `setupStep < 4`) and the persistent
 * page (when `setupStep >= 4`). The `step` search param selects the guided
 * step; a mismatch with the server's `setupStep` redirects to the correct
 * step (`flow.md`, B2--B3; `technical.md`, Dashboard routes).
 *
 * The persistent page groups the website's features by priority: what visitors
 * see (branding, welcome), how it's installed (name, domains, script), the plan
 * it runs on, and, last, deleting it. Each feature is a settings card with its
 * one action in a Paper Dim foot.
 */
// `card=added`: back from Paddle after adding a card for a paid plan (`WebsitePlanCard`).
const searchSchema = z.object({
  step: z.coerce.number().optional(),
  card: z.enum(['added']).optional(),
});

export const Route = createFileRoute('/_app/websites/$websiteId/setup')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: SetupPage,
});

function SetupPage() {
  const status = useContext(WebsiteStatusContext);
  const { websiteId } = Route.useParams();
  const { step } = Route.useSearch();
  const website = useWebsite(websiteId);
  const navigate = useNavigate();

  if (status === 'not-found') {
    return (
      <>
        <PageHeading>Website setup</PageHeading>
        <p className={styles.caption}>This website doesn&apos;t exist or was removed.</p>
      </>
    );
  }

  if (website.isPending) return <LoadingArea />;
  if (website.isError)
    return (
      <LoadFailedArea
        onRetry={() => {
          void website.refetch();
        }}
      />
    );

  const data = website.data;

  // Persistent page: setupStep >= 4 and no step param
  if (data.setupStep >= 4 && step === undefined) {
    return <PersistentPage website={data} />;
  }

  // Guided sequence: determine which step to show
  const expectedStep = data.setupStep + 1;
  if (step !== expectedStep) {
    // Redirect to the correct step (AC13)
    void navigate({
      to: '/websites/$websiteId/setup',
      params: { websiteId },
      search: data.setupStep >= 4 ? {} : { step: expectedStep },
      replace: true,
    });
    return <LoadingArea />;
  }

  if (step === 2) return <IndustryStep website={data} />;
  if (step === 3) return <BrandingStep website={data} />;
  if (step === 4) return <ScriptStep website={data} />;

  return <LoadingArea />;
}

// ---------------------------------------------------------------------------
// Guided steps: a shared shell with a progress bar and a single card
// ---------------------------------------------------------------------------

/** Four segments: the steps before this one are done, this one is the needle. */
function StepProgress({ step }: { step: number }) {
  return (
    <div className={styles.progress}>
      <p className={styles.progressLabel}>Step {step} of 4</p>
      <div className={styles.progressBar} aria-hidden="true">
        {[1, 2, 3, 4].map((n) => (
          <span
            key={n}
            className={styles.progressSeg}
            data-state={n < step ? 'done' : n === step ? 'current' : 'upcoming'}
          />
        ))}
      </div>
    </div>
  );
}

/** The guided-step frame: heading, progress, then one titled card. */
function GuidedStep({
  step,
  title,
  onSubmit,
  children,
  foot,
}: {
  step: number;
  title: string;
  onSubmit?: (e: FormEvent) => void;
  children: ReactNode;
  foot: ReactNode;
}) {
  return (
    <>
      <PageHeading>Website setup</PageHeading>
      <div className={styles.steps}>
        <StepProgress step={step} />
        <section className={styles.card} aria-label={title}>
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>{title}</h2>
          </div>
          {onSubmit ? (
            <form onSubmit={onSubmit}>
              <div className={styles.cardBody}>{children}</div>
              <div className={styles.cardFoot}>{foot}</div>
            </form>
          ) : (
            <>
              <div className={styles.cardBody}>{children}</div>
              <div className={styles.cardFoot}>{foot}</div>
            </>
          )}
        </section>
      </div>
    </>
  );
}

/** The industry <select>, drawn the same as a text field. */
function IndustrySelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: Industry;
  onChange: (industry: Industry) => void;
}) {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        Industry
      </label>
      <select
        id={id}
        className={styles.select}
        value={value}
        onChange={(e) => onChange(e.target.value as Industry)}
      >
        {INDUSTRIES.map((i) => (
          <option key={i} value={i}>
            {INDUSTRY_LABELS[i]}
          </option>
        ))}
      </select>
    </div>
  );
}

/** The live swatch beside an accent-colour field (neutral default when empty). */
function AccentSwatch({ hex }: { hex: string }) {
  const parsed = parseHexColour(hex.trim());
  return (
    <span
      className={`${styles.swatch} ${parsed ? '' : styles.swatchEmpty}`}
      style={parsed ? { backgroundColor: parsed } : undefined}
      aria-hidden="true"
    />
  );
}

// ---------------------------------------------------------------------------
// Step 2: Industry
// ---------------------------------------------------------------------------

function IndustryStep({ website }: { website: WebsiteResponse }) {
  const navigate = useNavigate();
  const update = useUpdateIndustry(website.id);
  const [industry, setIndustry] = useState<Industry>(website.industry);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    update.mutate(
      { industry },
      {
        onSuccess: () => {
          void navigate({
            to: '/websites/$websiteId/setup',
            params: { websiteId: website.id },
            search: { step: 3 },
          });
        },
      },
    );
  }

  return (
    <GuidedStep
      step={2}
      title="Your industry"
      onSubmit={onSubmit}
      foot={
        <>
          <p className={styles.footNote}>
            This sets the guardrails the assistant follows for your field.
          </p>
          <Button variant="primary" type="submit" disabled={update.isPending}>
            {update.isPending ? 'Saving...' : 'Continue'}
          </Button>
        </>
      }
    >
      <IndustrySelect id="industry" value={industry} onChange={setIndustry} />
      {update.isError ? (
        <DangerAlert title="Couldn't save">{(update.error as Error).message}</DangerAlert>
      ) : null}
    </GuidedStep>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Branding
// ---------------------------------------------------------------------------

function BrandingStep({ website }: { website: WebsiteResponse }) {
  const navigate = useNavigate();
  const updateBranding = useUpdateBranding(website.id);
  const skip = useSkipBranding(website.id);
  const [hex, setHex] = useState(website.accentColour ?? '');
  const [error, setError] = useState<string | undefined>();

  function toStep4() {
    void navigate({
      to: '/websites/$websiteId/setup',
      params: { websiteId: website.id },
      search: { step: 4 },
    });
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);

    const value = hex.trim();
    if (value === '') {
      updateBranding.mutate({ accentColour: null }, { onSuccess: toStep4 });
      return;
    }

    const parsed = parseHexColour(value);
    if (!parsed) {
      setError('Enter a valid hex colour, e.g. #1F6F5C.');
      return;
    }

    if (!meetsAccentContrast(parsed)) {
      setError(
        'This colour does not meet the 4.5:1 contrast requirement with white text. Choose a darker colour.',
      );
      return;
    }

    updateBranding.mutate({ accentColour: parsed }, { onSuccess: toStep4 });
  }

  return (
    <GuidedStep
      step={3}
      title="Branding"
      onSubmit={onSubmit}
      foot={
        <>
          <Button
            variant="secondary"
            type="button"
            onClick={() => skip.mutate(undefined, { onSuccess: toStep4 })}
            disabled={skip.isPending}
          >
            {skip.isPending ? 'Skipping...' : 'Skip for now'}
          </Button>
          <Button variant="primary" type="submit" disabled={updateBranding.isPending}>
            {updateBranding.isPending ? 'Saving...' : 'Continue'}
          </Button>
        </>
      }
    >
      <div className={styles.accentRow}>
        <TextField
          id="accent-colour"
          label="Accent colour"
          placeholder="#1F6F5C"
          hint="The widget's one colour. Leave it blank for the neutral default."
          value={hex}
          onChange={(e) => {
            setHex(e.target.value);
            setError(undefined);
          }}
          error={error}
        />
        <AccentSwatch hex={hex} />
      </div>
      {updateBranding.isError ? (
        <DangerAlert title="Couldn't save">{(updateBranding.error as Error).message}</DangerAlert>
      ) : null}
    </GuidedStep>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Script
// ---------------------------------------------------------------------------

function ScriptStep({ website }: { website: WebsiteResponse }) {
  const navigate = useNavigate();
  const complete = useCompleteSetup(website.id);
  const me = useMe();
  const hasKnowledge = me.data?.permissions.includes('knowledge') ?? false;

  function onFinish() {
    complete.mutate(undefined, {
      onSuccess: () => {
        void navigate({
          to: '/websites/$websiteId/setup',
          params: { websiteId: website.id },
          search: {},
        });
      },
    });
  }

  return (
    <GuidedStep
      step={4}
      title="Install the script"
      foot={
        <>
          <p className={styles.footNote}>
            {hasKnowledge
              ? 'Next, choose the pages the assistant uses.'
              : 'Someone with the knowledge permission chooses the pages the assistant uses.'}
          </p>
          <Button variant="primary" type="button" onClick={onFinish} disabled={complete.isPending}>
            {complete.isPending ? 'Finishing...' : 'Finish setup'}
          </Button>
        </>
      }
    >
      <ScriptBlock script={website.script} />
      <DomainChips domains={website.domains} />
      {complete.isError ? (
        <DangerAlert title="Couldn't finish setup">{(complete.error as Error).message}</DangerAlert>
      ) : null}
    </GuidedStep>
  );
}

// ---------------------------------------------------------------------------
// Persistent page (setupStep >= 4)
// ---------------------------------------------------------------------------

function PersistentPage({ website }: { website: WebsiteResponse }) {
  const me = useMe();
  const hasKnowledge = me.data?.permissions.includes('knowledge') ?? false;

  return (
    <>
      <PageHeading>Website setup</PageHeading>
      <p className={styles.caption}>
        Everything for {website.name}: what visitors see, how it&apos;s installed, and the plan it
        runs on.
      </p>

      <div className={styles.page}>
        {!website.hasContactDetails ? (
          <ContactDetailsNotice hasKnowledge={hasKnowledge} websiteId={website.id} />
        ) : null}

        <div className={styles.grid}>
          <WebsiteCard website={website} />
          <SuggestedQuestionsCard website={website} />
        </div>

        <ScriptCard website={website} hasKnowledge={hasKnowledge} />

        <WebsitePlanCard websiteId={website.id} />

        <section className={styles.group} aria-labelledby="group-danger">
          <h2 id="group-danger" className={styles.groupTitle}>
            Danger zone
          </h2>
          <DeleteCard website={website} />
        </section>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared card parts
// ---------------------------------------------------------------------------

/** The embed snippet, on an Ink block with a copy button. */
function ScriptBlock({ script }: { script: string | null }) {
  const [copied, setCopied] = useState(false);

  function onCopy() {
    if (script) {
      void navigator.clipboard.writeText(script).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }

  return (
    <div className={styles.codeBlock}>
      <code>{script}</code>
      <button type="button" className={styles.copyButton} onClick={onCopy} aria-label="Copy script">
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </div>
  );
}

function DomainChips({ domains }: { domains: readonly string[] }) {
  return (
    <div className={styles.chips}>
      {domains.map((d) => (
        <span key={d} className={styles.chip}>
          {d}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact details notice (AC26--AC30, AC40)
// ---------------------------------------------------------------------------

function ContactDetailsNotice({
  hasKnowledge,
  websiteId,
}: {
  hasKnowledge: boolean;
  websiteId: string;
}) {
  return (
    <div className={styles.notice} role="alert">
      <AlertTriangle className={styles.noticeIcon} aria-hidden="true" strokeWidth={1.75} />
      <div>
        <strong className={styles.noticeTitle}>No contact details found</strong>
        <p className={styles.noticeBody}>
          {hasKnowledge
            ? 'The assistant cannot show contact information to visitors.'
            : 'Someone with the knowledge permission selects a page or adds a custom page that has a phone number or contact form.'}
        </p>
        {hasKnowledge ? (
          <div className={styles.noticeAction}>
            <Link
              to="/websites/$websiteId/knowledge"
              params={{ websiteId }}
              className={`${buttonStyles.button} ${buttonStyles.secondary}`}
            >
              Select pages
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Website card (name and domains)
// ---------------------------------------------------------------------------

/**
 * The website's identity and branding in one card: its name and domains, the
 * industry that sets the assistant's guardrails, and the widget accent colour.
 * One Save persists whatever changed; a new industry is confirmed first,
 * because it changes the guardrails for new conversations.
 */
function WebsiteCard({ website }: { website: WebsiteResponse }) {
  const updateWebsite = useUpdateWebsite(website.id);
  const updateIndustry = useUpdateIndustry(website.id);
  const updateBranding = useUpdateBranding(website.id);
  const [name, setName] = useState(website.name);
  const [domains, setDomains] = useState(website.domains.join(', '));
  const [industry, setIndustry] = useState<Industry>(website.industry);
  const [hex, setHex] = useState(website.accentColour ?? '');
  const [error, setError] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);
  // The accent colour checked on Save, held while the industry change is confirmed.
  const [pendingAccent, setPendingAccent] = useState<string | null | undefined>(undefined);

  const isSaving = updateWebsite.isPending || updateIndustry.isPending || updateBranding.isPending;
  const saveError = updateWebsite.error ?? updateIndustry.error ?? updateBranding.error;

  /** The checked accent colour (`null` clears it), or `undefined` when it isn't valid. */
  function checkedAccent(): string | null | undefined {
    const value = hex.trim();
    if (value === '') return null;
    const parsed = parseHexColour(value);
    if (!parsed) {
      setError('Enter a valid hex colour, e.g. #1F6F5C.');
      return undefined;
    }
    if (!meetsAccentContrast(parsed)) {
      setError(
        'This colour does not meet the 4.5:1 contrast requirement with white text. Choose a darker colour.',
      );
      return undefined;
    }
    return parsed;
  }

  async function save(accent: string | null) {
    try {
      const domainList = domains
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean);
      await updateWebsite.mutateAsync({ name: name.trim(), domains: domainList });
      if (industry !== website.industry) await updateIndustry.mutateAsync({ industry });
      if (accent !== (website.accentColour ?? null)) {
        await updateBranding.mutateAsync({ accentColour: accent });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // The failed mutation's error shows in the card.
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    const accent = checkedAccent();
    if (accent === undefined) return;
    // A new industry applies a different guardrail set, so it is confirmed first.
    if (industry !== website.industry) {
      setPendingAccent(accent);
      return;
    }
    void save(accent);
  }

  return (
    <section className={styles.card} aria-labelledby="card-website">
      <div className={styles.cardHead}>
        <h3 id="card-website" className={styles.cardTitle}>
          Website and branding
        </h3>
      </div>
      <form onSubmit={onSubmit}>
        <div className={styles.cardBody}>
          <TextField
            id="website-name"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            id="website-domains"
            label="Domains"
            hint="Separate several with commas."
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
          />
          <IndustrySelect id="persistent-industry" value={industry} onChange={setIndustry} />
          <div className={styles.accentRow}>
            <TextField
              id="persistent-accent"
              label="Accent colour"
              placeholder="#1F6F5C"
              hint="Leave blank for the neutral default."
              value={hex}
              onChange={(e) => {
                setHex(e.target.value);
                setError(undefined);
              }}
              error={error}
            />
            <AccentSwatch hex={hex} />
          </div>
          {saveError ? (
            <DangerAlert title="Couldn't save">{(saveError as Error).message}</DangerAlert>
          ) : null}
        </div>
        <div className={styles.cardFoot}>
          <SavedCaption show={saved} />
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>

      {pendingAccent !== undefined ? (
        <IndustryConfirmDialog
          newIndustry={industry}
          onConfirm={() => {
            const accent = pendingAccent;
            setPendingAccent(undefined);
            void save(accent);
          }}
          onCancel={() => setPendingAccent(undefined)}
        />
      ) : null}
    </section>
  );
}

function SavedCaption({ show }: { show: boolean }) {
  return (
    <p className={styles.savedCaption} role="status">
      {show ? (
        <>
          <CircleCheck className={styles.captionIcon} aria-hidden="true" strokeWidth={1.75} />
          Saved
        </>
      ) : null}
    </p>
  );
}

function IndustryConfirmDialog({
  newIndustry,
  onConfirm,
  onCancel,
}: {
  newIndustry: Industry;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className={pageStyles.dialogOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Change industry"
    >
      <div className={pageStyles.dialog}>
        <h3>Change industry?</h3>
        <p>
          Changing to <strong>{INDUSTRY_LABELS[newIndustry]}</strong> applies a different guardrail
          set to new conversations. Stored conversations are not changed.
        </p>
        <div className={pageStyles.buttonRow}>
          <Button variant="primary" onClick={onConfirm}>
            Change industry
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Script card
// ---------------------------------------------------------------------------

function ScriptCard({
  website,
  hasKnowledge,
}: {
  website: WebsiteResponse;
  hasKnowledge: boolean;
}) {
  return (
    <section className={styles.card} aria-labelledby="card-script">
      <div className={styles.cardHead}>
        <h3 id="card-script" className={styles.cardTitle}>
          Script
        </h3>
      </div>
      <div className={styles.cardBody}>
        <ScriptBlock script={website.script} />
        <DomainChips domains={website.domains} />
        <p className={styles.hint}>
          {hasKnowledge
            ? 'Paste this before the closing body tag on every page above. Then choose the pages the assistant uses in Knowledge.'
            : 'Paste this before the closing body tag on every page above. Someone with the knowledge permission chooses the pages the assistant uses.'}
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Delete card (AC31--AC36, AC41--AC42)
// ---------------------------------------------------------------------------

function DeleteCard({ website }: { website: WebsiteResponse }) {
  const [showDialog, setShowDialog] = useState(false);

  return (
    <section className={`${styles.card} ${styles.dangerCard}`} aria-labelledby="card-delete">
      <div className={styles.cardHead}>
        <h3 id="card-delete" className={styles.cardTitle}>
          Delete website
        </h3>
      </div>
      <div className={styles.cardBody}>
        <p className={styles.hint}>
          The assistant stops at once on every domain. Its data is removed after 90 days and
          can&apos;t be restored.
        </p>
      </div>
      <div className={styles.cardFoot}>
        <Button variant="danger" onClick={() => setShowDialog(true)}>
          Delete website
        </Button>
      </div>

      {showDialog ? (
        <DeleteWebsiteDialog website={website} onClose={() => setShowDialog(false)} />
      ) : null}
    </section>
  );
}

function DeleteWebsiteDialog({
  website,
  onClose,
}: {
  website: WebsiteResponse;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const del = useDeleteWebsite(website.id);
  const [confirmDomain, setConfirmDomain] = useState('');
  const [error, setError] = useState<string | undefined>();

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);

    const normalizedConfirm = confirmDomain.trim().toLowerCase();
    const matches = website.domains.some((d) => d.toLowerCase() === normalizedConfirm);
    if (!matches) {
      setError('Type one of the domains listed above to confirm.');
      return;
    }

    del.mutate(
      { confirmDomain: confirmDomain.trim() },
      {
        onSuccess: () => {
          void navigate({ to: '/', replace: true });
        },
      },
    );
  }

  return (
    <div
      className={pageStyles.dialogOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={`Delete ${website.name}`}
    >
      <div className={pageStyles.dialog}>
        <h3>Delete {website.name}?</h3>
        <p>
          The assistant stops at once on every domain below. Its website data is removed after 90
          days. There&apos;s no way to restore it.
        </p>
        <ul className={pageStyles.domainList}>
          {website.domains.map((d) => (
            <li key={d}>
              <code>{d}</code>
            </li>
          ))}
        </ul>
        <form onSubmit={onSubmit}>
          <TextField
            id="confirm-domain"
            label="Type one of these domains to confirm"
            placeholder="Not case-sensitive"
            value={confirmDomain}
            onChange={(e) => {
              setConfirmDomain(e.target.value);
              setError(undefined);
            }}
            error={error}
            autoFocus
          />
          {del.isError ? (
            <DangerAlert title="Couldn't delete">{(del.error as Error).message}</DangerAlert>
          ) : null}
          <div className={pageStyles.buttonRow}>
            <Button variant="danger" type="submit" disabled={del.isPending}>
              {del.isPending ? 'Deleting...' : 'Delete website'}
            </Button>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
